import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { BarChart3, Building2, Database, Home, LogOut, RefreshCw, Upload, UserPlus, Users } from "lucide-react";
import AdminOverviewPage from "@/pages/admin/sections/AdminOverviewPage";
import AdminUsersPage from "@/pages/admin/sections/AdminUsersPage";
import AdminAcademyPage from "@/pages/admin/sections/AdminAcademyPage";
import AdminDatabasePage from "@/pages/admin/sections/AdminDatabasePage";

export default function AdminDashboardView({
  navigate,
  fetchCollections,
  fetchCenters,
  fetchUnassignedStudents,
  fetchAnalytics,
  fetchUserActivity,
  setCreateCoachMode,
  fetchCoaches,
  setImportMode,
  handleLogout,
  newCenterData,
  setNewCenterData,
  handleCreateCenter,
  centersList,
  selectedCenterForBatch,
  setSelectedCenterForBatch,
  fetchCenterBatches,
  newBatchName,
  setNewBatchName,
  handleCreateBatch,
  centerBatches,
  bulkMappingData,
  setBulkMappingData,
  fetchMappingBatches,
  mappingBatches,
  selectedStudentIds,
  unassignedStudents,
  handleSelectAllUnassigned,
  toggleStudentSelection,
  handleBulkAssignStudents,
  collections,
  selectedCollection,
  setSelectedCollection,
  fetchDocuments,
  totalDocs,
  viewMode,
  setViewMode,
  createMode,
  setCreateMode,
  newDocData,
  setNewDocData,
  handleCreateDoc,
  searchQuery,
  setSearchQuery,
  loading,
  documents,
  getDocKeys,
  getFilteredDocs,
  formatValue,
  handleResetPassword,
  handleViewDoc,
  handleDeleteDoc,
  analytics,
  userActivity,
}) {
  const [activePage, setActivePage] = useState("overview");

  const navItems = useMemo(
    () => [
      { id: "overview", label: "Overview", icon: Home },
      { id: "users", label: "Users", icon: Users },
      { id: "academy", label: "Academy", icon: Building2 },
      { id: "database", label: "Database", icon: Database },
    ],
    []
  );

  const refreshAll = () => {
    fetchCollections();
    fetchCenters();
    fetchUnassignedStudents();
    fetchAnalytics();
    fetchUserActivity();
  };

  const renderPage = () => {
    if (activePage === "overview") {
      return <AdminOverviewPage analytics={analytics} userActivity={userActivity} />;
    }

    if (activePage === "users") {
      return <AdminUsersPage userActivity={userActivity} handleResetPassword={handleResetPassword} />;
    }

    if (activePage === "academy") {
      return (
        <AdminAcademyPage
          newCenterData={newCenterData}
          setNewCenterData={setNewCenterData}
          handleCreateCenter={handleCreateCenter}
          centersList={centersList}
          selectedCenterForBatch={selectedCenterForBatch}
          setSelectedCenterForBatch={setSelectedCenterForBatch}
          fetchCenterBatches={fetchCenterBatches}
          newBatchName={newBatchName}
          setNewBatchName={setNewBatchName}
          handleCreateBatch={handleCreateBatch}
          centerBatches={centerBatches}
          bulkMappingData={bulkMappingData}
          setBulkMappingData={setBulkMappingData}
          fetchMappingBatches={fetchMappingBatches}
          mappingBatches={mappingBatches}
          selectedStudentIds={selectedStudentIds}
          unassignedStudents={unassignedStudents}
          handleSelectAllUnassigned={handleSelectAllUnassigned}
          toggleStudentSelection={toggleStudentSelection}
          handleBulkAssignStudents={handleBulkAssignStudents}
        />
      );
    }

    return (
      <AdminDatabasePage
        collections={collections}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        fetchDocuments={fetchDocuments}
        totalDocs={totalDocs}
        viewMode={viewMode}
        setViewMode={setViewMode}
        createMode={createMode}
        setCreateMode={setCreateMode}
        newDocData={newDocData}
        setNewDocData={setNewDocData}
        handleCreateDoc={handleCreateDoc}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        loading={loading}
        documents={documents}
        getDocKeys={getDocKeys}
        getFilteredDocs={getFilteredDocs}
        formatValue={formatValue}
        handleResetPassword={handleResetPassword}
        handleViewDoc={handleViewDoc}
        handleDeleteDoc={handleDeleteDoc}
      />
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex min-h-screen">
        <aside className="w-72 bg-slate-950 text-slate-100 p-5 flex flex-col">
          <div className="flex items-center gap-3 pb-5 border-b border-slate-800">
            <img src="/gfa-logo.png?v=5" alt="GFA Logo" className="w-11 h-11 object-contain rounded-full bg-white p-0.5" />
            <div>
              <p className="font-semibold tracking-wide">Control Room</p>
              <p className="text-xs text-slate-400">Admin Operations</p>
            </div>
          </div>

          <nav className="pt-5 space-y-2 flex-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = activePage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActivePage(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                    active ? "bg-indigo-500 text-white" : "text-slate-300 hover:bg-slate-900"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="space-y-2 pt-4 border-t border-slate-800">
            <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => setCreateCoachMode(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Create Coach
            </Button>
            <Button
              size="sm"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                fetchCoaches();
                setImportMode(true);
              }}
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Admin Dashboard</h1>
              <p className="text-sm text-slate-500">Manage users, academy setup, and data in dedicated pages</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={refreshAll}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/login")}>
                <BarChart3 className="w-4 h-4 mr-2" />
                Back to App
              </Button>
              <Button size="sm" variant="destructive" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </header>

          <section className="p-6">{renderPage()}</section>
        </main>
      </div>
    </div>
  );
}
